import { sendExceptionToSentry } from 'shared/helpers/sentry';

const LOG_API = process.env.VUE_APP_HIVER_CHANNELS_MIDDLEWARE_V2 + '/v2/metrics/log-metrics';
const version = process.env.VUE_APP_SENTRY_RELEASE || 'unknown';

const API_KEYS = [
  { path: '/widget/config', name: 'widget-config' },
  { path: /widget\/\w+\/config/, name: 'hiver-config' },
  { path: '/widget/campaigns', name: 'campaigns' },
  { path: '/widget/conversations', name: 'conversations' },
  { path: '/widget/inbox_members', name: 'widget-inbox_members' },
  { path: '/widget/events', name: 'events' },
  { path: '/widget/messages', name: 'messages' },
  { path: '/widget/contact', name: 'contact' },
  { path: '/widget/labels', name: 'labels' },
  { path: '/widget/conversations/toggle_status', name: 'toggle_status'},
  { path: 'send_contact_email', name: 'send_contact_email'},
  { path: /inbox\/\w+\/trigger_bot/, name: 'trigger_bot' },
  { path: /inbox\/\w+\/restart_bot/, name: 'restart_bot' }
]


const trackApi = (apiPath) => {
  for( let { path, name } of API_KEYS ){
    if (
      ( typeof path === 'string' && apiPath.includes(path) ) ||
      ( typeof path.test === 'function' && path.test(apiPath) )
    ){
      return { name, track: true };
    }
  }
  return { track: false }
}

const generateSessionId = () => {
  if ('crypto' in window && 'randomUUID' in window.crypto) {
    return window.crypto.randomUUID();
  }
  const timestamp = Date.now().toString(16);
  const randomPart = Math.random().toString(16).substring(2, 8);
  return `${timestamp}-${randomPart}`;
}

const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    const browserRegexPatterns = [
      ['Edge', /Edg\/(\d+)/],
      ['Chrome', /Chrome\/(\d+)/],
    ];

    for (const [name, pattern] of browserRegexPatterns) {
      const match = pattern.exec(userAgent);
      if (match) {
        return { browserName: name, browserVersion: match[1] };
      }
    }
    // incase nothing matches
    return {}
}

const getOS = () => {
  const userAgent = navigator?.userAgent;
  let os = "Unknown";

  if( !userAgent ){
    return os;
  }

  if (userAgent.indexOf("Win") !== -1) os = "Windows";
  if (userAgent.indexOf("Mac") !== -1) os = "MacOS";
  if (userAgent.indexOf("Linux") !== -1) os = "Linux";
  if (userAgent.indexOf("Android") !== -1) os = "Android";
  if (userAgent.indexOf("iOS") !== -1) os = "iOS";
  return os;
}

const getOrigin = () => {
  const isIFrame = window.self !== window.top;
  const url = isIFrame ? document.referrer : window.origin;
  if (!url) return 'https://hiver-testsuite.com/'; // this is for QA automations since document.referrer is not available
  return window.URL ? new window.URL(url).origin : url;
}


class LogMonad {
  constructor({ allLogs = [], logs = [], meta = { }  }) {
    this.allLogs = allLogs;
    this.logs = logs;
    this.meta = meta;
  }

  log(logFn){
    const data = logFn();
    const logData = {
      ...this.meta,
      ...data
    }
    return new LogMonad({
      allLogs: [ ...this.allLogs, logData ],
      logs: [ ...this.logs, logData ],
      meta: { ...this.meta }
    } )
  }

  flush(flushFn){
    flushFn([...this.logs]);
    return new LogMonad({
      allLogs: [ ...this.allLogs ],
      logs: [],
      meta: { ...this.meta }
    } )
  }

  addMeta(metaFn) {
    const meta = metaFn({ ...this.meta });

    return new LogMonad({
      allLogs: [...this.allLogs],
      logs: [...this.logs],
      meta: {...this.meta, ...meta }
    })
  }

}

class LogMonadNoop{
  constructor(){
    this.allLogs = [];
    this.logs = [];
    this.meta = {};
  }

  log(){
    return this;
  }

  flush(){
    return this;
  }

  addMeta(){
    return this;
  }
}

const Logger = process.env.VUE_APP_LOGS_ENABLED === 'true' ? LogMonad : LogMonadNoop;

let logger = new Logger({}).addMeta(() => {
  const sessionId = generateSessionId();
  const osDetails = getOS();
  const origin = getOrigin();
  const browserInfo = getBrowserInfo();
  return { sessionId, osDetails, origin, version, ...browserInfo };
});

/**
 * later can include `web-vitals` lib to track CLS,FID,LCP;
 */
const observer = new PerformanceObserver((list) => {
  list.getEntries().forEach(entry => {
    const { duration, initiatorType, responseStatus, name } = entry;
    let apiData;
    switch (entry.entryType) {
      case 'paint':
        logger = logger.log(() => ({
          type: 'paint',
          name: entry.name,
          startTime: entry.startTime
        }));
        break;
      case 'resource':
        apiData = trackApi(name);
        if( !apiData.track ){
          break;
        }
        logger = logger.log(() => ({
          type: 'resource',
          name: apiData.name,
          duration,
          responseStatus,
          initiatorType
        }));
        break;
    }
  });
});

observer.observe({ entryTypes: [ "paint", "resource" ] });

const flushLogs = () => {
  logger = logger.flush((logs) => {
    if (!logs.length) {
      return;
    }
    try {
      console.debug('sending logs');
      const data = new Blob([JSON.stringify(logs)], { type: "application/json" });
      navigator.sendBeacon(LOG_API, data);
    }
    catch(error){
      console.debug('logs sending failed');
      sendExceptionToSentry(error);
    }
  })
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === "hidden") {
    flushLogs();
  }
});

document.addEventListener('beforeunload', flushLogs);

export const loggerInstance = {
  EVENTS: {
    NEW_CONVERSATION_BOT: 'new-conversation-bot',
    NEW_CONVERSATION_AGENT: 'new-conversation-agent'
  },
  addLog: ( logData ) => {
    logger = logger.log(() => ({ ...logData }));
  },
  addMeta: ( extra ) => {
    logger = logger.addMeta(( meta ) => ({ extra: { ...meta.extra, ...extra } }));
  }
}
